import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Typography,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  SelectChangeEvent,
  InputAdornment,
} from '@mui/material';
import { Add, Edit, Delete, Business, LinkOff, Search } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Usuario, Tenant } from '../types';
import { usuarioService } from '../services/usuarioService';
import { tenantService } from '../services/tenantService';
import { getErrorMessage } from '../utils/errors';
import { useAuth } from '../contexts/AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';

const perfilColors: Record<string, 'primary' | 'secondary' | 'success' | 'warning'> = {
  SUPER_ADMIN: 'warning',
  ADMIN: 'primary',
  GERENTE: 'secondary',
  MOTORISTA: 'success',
};

const schema = yup.object({
  nome: yup.string().required('Nome é obrigatório'),
  email: yup.string().required('Email é obrigatório').email('Email inválido'),
  perfil: yup
    .string()
    .required('Perfil é obrigatório')
    .oneOf(['ADMIN', 'GERENTE', 'MOTORISTA'], 'Perfil inválido'),
  senha: yup.string().when('editing', {
    is: true,
    then: (s) => s.notRequired(),
    otherwise: (s) => s.required('Senha é obrigatória').min(6, 'Senha deve ter no mínimo 6 caracteres'),
  }),
}).required();

const Usuarios: React.FC = () => {
  const { user: currentUser, hasRole } = useAuth();
  const ehAdmin = hasRole('ADMIN') || hasRole('SUPER_ADMIN') || hasRole('GERENTE');
  const ehSuperAdmin = hasRole('SUPER_ADMIN');
  const ehGestorTenant = hasRole('ADMIN') || hasRole('GERENTE');

  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<number | ''>('');
  const [open, setOpen] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState<Usuario | null>(null);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [senhaGerada, setSenhaGerada] = useState<{ nome: string; email: string; senha: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [confirmDesvincular, setConfirmDesvincular] = useState<{ open: boolean; usuario: Usuario | null }>({ open: false, usuario: null });
  const [filtro, setFiltro] = useState('');
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [tenantUsuario, setTenantUsuario] = useState<Usuario | null>(null);
  const [tenantSelecionados, setTenantSelecionados] = useState<number[]>([]);

  const { register, control, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (ehSuperAdmin) {
      tenantService.listar().then(setTenants).catch(console.error);
    }
    loadUsuarios();
  }, []);

  useEffect(() => {
    loadUsuarios();
  }, [selectedTenantId]);

  const loadUsuarios = async () => {
    try {
      let data: Usuario[];
      if (ehSuperAdmin && selectedTenantId !== '') {
        data = await usuarioService.listarPorTenant(selectedTenantId as number);
      } else if (ehSuperAdmin) {
        data = await usuarioService.listarTodos();
      } else {
        data = await usuarioService.listar();
      }
      setUsuarios(ehSuperAdmin ? data : data.filter((usuario) => usuario.perfil !== 'SUPER_ADMIN'));
    } catch (error) {
      console.error('Erro ao carregar usuários', error);
    }
  };

  const handleOpen = (usuario?: Usuario) => {
    if (usuario) {
      setEditingUsuario(usuario);
      reset({ ...usuario, senha: '', editing: true } as any);
      setTenantSelecionados(usuario.tenantIds || (usuario.tenantId ? [usuario.tenantId] : []));
    } else {
      setEditingUsuario(null);
      reset({ nome: '', email: '', perfil: 'MOTORISTA', senha: '', editing: false } as any);
      setTenantSelecionados(selectedTenantId !== '' ? [selectedTenantId as number] : []);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingUsuario(null);
    reset();
    setErro('');
    setTenantSelecionados([]);
  };

  const handleTenantChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value as number[];
    setTenantSelecionados(value);
  };

  const onSubmit = async (data: any) => {
    const { editing, ...payload } = data;
    setSalvando(true);
    try {
      if (editingUsuario?.id) {
        payload.tenantIds = tenantSelecionados;
        await usuarioService.atualizar(editingUsuario.id, payload);
        loadUsuarios();
        handleClose();
      } else {
        payload.tenantIds = tenantSelecionados.length > 0 ? tenantSelecionados : (selectedTenantId !== '' ? [selectedTenantId as number] : []);
        const resultado = await usuarioService.cadastrar(payload);
        loadUsuarios();
        setOpen(false);
        setEditingUsuario(null);
        reset();
        setTenantSelecionados([]);
        setSenhaGerada({
          nome: payload.nome,
          email: payload.email,
          senha: resultado.dados?.senhaGerada || '',
        });
      }
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  };

  const handleDelete = (id: number) => {
    setConfirmDelete({ open: true, id });
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete.id) return;
    try {
      await usuarioService.desativar(confirmDelete.id);
      loadUsuarios();
      setConfirmDelete({ open: false, id: null });
    } catch (error) {
      console.error('Erro ao desativar usuário', error);
      setErro(getErrorMessage(error));
      setConfirmDelete({ open: false, id: null });
    }
  };

  const confirmarDesvincular = async () => {
    if (!confirmDesvincular.usuario?.id) return;
    const tenantIdAlvo = ehSuperAdmin
      ? (confirmDesvincular.usuario.tenantId || confirmDesvincular.usuario.tenantIds?.[0])
      : (currentUser?.tenantIdSelecionado || currentUser?.tenantId || currentUser?.tenants?.find((t: any) => t.padrao)?.id || currentUser?.tenants?.[0]?.id);
    if (!tenantIdAlvo) {
      setErro('Não foi possível identificar a organização atual');
      setConfirmDesvincular({ open: false, usuario: null });
      return;
    }
    try {
      await usuarioService.removerTenant(confirmDesvincular.usuario.id, tenantIdAlvo);
      loadUsuarios();
      setConfirmDesvincular({ open: false, usuario: null });
    } catch (error) {
      console.error('Erro ao desvincular usuário', error);
      setErro(getErrorMessage(error));
      setConfirmDesvincular({ open: false, usuario: null });
    }
  };

  const handleOpenTenants = (usuario: Usuario) => {
    setTenantUsuario(usuario);
    setTenantSelecionados(usuario.tenantIds || (usuario.tenantId ? [usuario.tenantId] : []));
    setTenantDialogOpen(true);
  };

  const handleSalvarTenants = async () => {
    if (!tenantUsuario?.id) return;
    setSalvando(true);
    try {
      await usuarioService.atualizar(tenantUsuario.id, {
        ...tenantUsuario,
        tenantIds: tenantSelecionados,
      });
      setTenantDialogOpen(false);
      setTenantUsuario(null);
      loadUsuarios();
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  };

  const termo = filtro.trim().toLowerCase();
  const usuariosFiltrados = termo
    ? usuarios.filter((item) => Object.values(item).filter(Boolean).some((campo) => String(campo).toLowerCase().includes(termo)))
    : usuarios;

  return (
    <Container disableGutters maxWidth={false}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Usuários</Typography>
        <Box display="flex" gap={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Pesquisar"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
          {ehAdmin && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpen()}
            >
              Novo Usuário
            </Button>
          )}
        </Box>
      </Box>

      {ehSuperAdmin && (
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Filtrar por Organização</InputLabel>
          <Select
            value={selectedTenantId}
            label="Filtrar por Organização"
            onChange={(e) => setSelectedTenantId(e.target.value as number | '')}
          >
            <MenuItem value="">
              <em>Todas as organizações</em>
            </MenuItem>
            {tenants.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.nome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      {senhaGerada && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSenhaGerada(null)}>
          <Typography variant="subtitle2">
            Usuário {senhaGerada.nome} cadastrado com sucesso!
          </Typography>
          <Typography variant="body2">
            <strong>Email:</strong> {senhaGerada.email} | <strong>Senha gerada:</strong>{' '}
            <code>{senhaGerada.senha}</code>
          </Typography>
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Email</TableCell>
              {ehSuperAdmin && <TableCell>Organizações</TableCell>}
              <TableCell>Perfil</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usuariosFiltrados.map((usuario) => (
              <TableRow key={usuario.id}>
                <TableCell><strong>{usuario.nome}</strong></TableCell>
                <TableCell sx={{ maxWidth: 250, wordBreak: 'break-word' }}>{usuario.email}</TableCell>
                {ehSuperAdmin && (
                  <TableCell>
                    {usuario.tenants && usuario.tenants.length > 0 ? (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {usuario.tenants.map((t) => (
                          <Chip key={t.id} label={t.nome} size="small" variant="outlined" />
                        ))}
                        <IconButton size="small" onClick={() => handleOpenTenants(usuario)} title="Gerenciar organizações">
                          <Business fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">—</Typography>
                        <IconButton size="small" onClick={() => handleOpenTenants(usuario)} title="Vincular organização">
                          <Business fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <Chip
                    label={usuario.perfil}
                    color={perfilColors[usuario.perfil] || 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={usuario.ativo ? 'Ativo' : 'Inativo'}
                    color={usuario.ativo ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(usuario)}>
                    <Edit />
                  </IconButton>
                  {ehSuperAdmin ? (
                    <>
                      {ehSuperAdmin && usuario.tenants && usuario.tenants.length > 1 && (
                        <IconButton
                          size="small"
                          color="warning"
                          title="Desvincular da organização atual"
                          onClick={() => setConfirmDesvincular({ open: true, usuario })}
                        >
                          <LinkOff />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        disabled={usuario.id === currentUser?.id}
                        title={usuario.id === currentUser?.id ? 'Você não pode desativar seu próprio usuário' : ''}
                        onClick={() => usuario.id && handleDelete(usuario.id)}
                      >
                        <Delete />
                      </IconButton>
                    </>
                  ) : ehGestorTenant ? (
                    <>
                      {usuario.tenants && usuario.tenants.length > 1 ? (
                        <IconButton
                          size="small"
                          color="warning"
                          title="Desvincular da organização atual"
                          onClick={() => setConfirmDesvincular({ open: true, usuario })}
                        >
                          <LinkOff />
                        </IconButton>
                      ) : null}
                      {(usuario.perfil === 'ADMIN' || usuario.perfil === 'GERENTE') && (usuario.tenants?.length || 0) <= 1 ? (
                        <IconButton
                          size="small"
                          color="error"
                          disabled={usuario.id === currentUser?.id}
                          title={usuario.id === currentUser?.id ? 'Você não pode desativar seu próprio usuário' : ''}
                          onClick={() => usuario.id && handleDelete(usuario.id)}
                        >
                          <Delete />
                        </IconButton>
                      ) : null}
                    </>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUsuario ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
        <DialogContent>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
          <form id="usuario-form" onSubmit={handleSubmit(onSubmit as any)}>
            {ehSuperAdmin && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Organizações</InputLabel>
                <Select
                  multiple
                  value={tenantSelecionados}
                  onChange={handleTenantChange}
                  input={<OutlinedInput label="Organizações" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const tenant = tenants.find((t) => t.id === value);
                        return <Chip key={value} label={tenant?.nome || value} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {tenants.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.nome}
                    </MenuItem>
                  ))}
                </Select>
                {!editingUsuario && <Typography variant="caption" color="text.secondary">Selecione uma ou mais organizações</Typography>}
              </FormControl>
            )}

            <TextField
              fullWidth
              label="Nome"
              {...register('nome')}
              error={!!errors.nome}
              helperText={errors.nome?.message as string}
              margin="normal"
            />

            <TextField
              fullWidth
              label="Email"
              type="email"
              {...register('email')}
              error={!!errors.email}
              helperText={errors.email?.message as string}
              margin="normal"
            />

            <Controller
              name="perfil"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  fullWidth
                  label="Perfil"
                  name={field.name}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  error={!!errors.perfil}
                  helperText={errors.perfil?.message as string}
                  margin="normal"
                  disabled={!!editingUsuario?.id && editingUsuario.perfil === 'SUPER_ADMIN'}
                >
                  {ehSuperAdmin && <MenuItem value="ADMIN">Administrador</MenuItem>}
                  {ehSuperAdmin && <MenuItem value="GERENTE">Gerente</MenuItem>}
                  {(ehSuperAdmin || hasRole('ADMIN')) && <MenuItem value="ADMIN">Administrador</MenuItem>}
                  <MenuItem value="GERENTE">Gerente</MenuItem>
                  <MenuItem value="MOTORISTA">Motorista</MenuItem>
                </TextField>
              )}
            />

            <TextField
              fullWidth
              label="Senha"
              type="password"
              {...register('senha')}
              error={!!errors.senha}
              helperText={
                (errors.senha?.message as string) ||
                (editingUsuario ? 'Deixe vazio para manter a senha atual' : '')
              }
              margin="normal"
              autoComplete="new-password"
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button type="submit" form="usuario-form" variant="contained" disabled={salvando}>
            {salvando ? 'Salvando...' : (editingUsuario ? 'Atualizar' : 'Cadastrar')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tenantDialogOpen} onClose={() => setTenantDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Organizações - {tenantUsuario?.nome}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Organizações</InputLabel>
            <Select
              multiple
              value={tenantSelecionados}
              onChange={handleTenantChange}
              input={<OutlinedInput label="Organizações" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const tenant = tenants.find((t) => t.id === value);
                    return <Chip key={value} label={tenant?.nome || value} size="small" />;
                  })}
                </Box>
              )}
            >
              {tenants.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTenantDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSalvarTenants} disabled={salvando}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete.open}
        title="Confirmar desativação"
        message="Tem certeza que deseja desativar este usuário? Ele não poderá mais acessar o sistema."
        confirmText="Desativar"
        severity="error"
        onConfirm={confirmDeleteAction}
        onCancel={() => setConfirmDelete({ open: false, id: null })}
      />

      <ConfirmDialog
        open={confirmDesvincular.open}
        title="Desvincular da organização"
        message="Tem certeza que deseja desvincular este usuário da organização atual? Se não houver outro vínculo, o usuário será desativado."
        confirmText="Desvincular"
        severity="warning"
        onConfirm={confirmarDesvincular}
        onCancel={() => setConfirmDesvincular({ open: false, usuario: null })}
      />
    </Container>
  );
};

export default Usuarios;
